import ExcelJS from "exceljs";
import { 
  Cadet, 
  CycleData, 
  CycleName, 
  ExcelCellValue, 
  ExcelRowData, 
  ValidationResult,
  CadetWithoutDisqualification 
} from "../../shared/types/scoreboard.types";
import { BUSINESS_RULES, CYCLE_PREFIXES, EXCEL_HEADERS } from "../../shared/constants";

/**
 * Excel parsing and validation service
 */
export class ExcelService {
  /**
   * Parse Excel data from the specified file path
   */
  static async parseExcelData(filePath: string): Promise<Cadet[]> {
    try {
      const response = await fetch(filePath);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error("Excel file contains no worksheets");
      }

      ExcelService.validateExcelStructure(worksheet);

      const headers = worksheet.getRow(1).values as string[];
      const cadetData: Cadet[] = [];

      worksheet.eachRow((row: ExcelJS.Row, rowNumber: number) => {
        if (rowNumber === 1) return; // Skip header row

        try {
          const rowData: ExcelRowData = {};
          row.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell, colNumber: number) => {
            const header = headers[colNumber];
            if (header) {
              // Handle CellErrorValue by converting to string or null
              if (cell.type === ExcelJS.ValueType.Error) {
                const errorValue = cell.value;
                if (errorValue && typeof errorValue === 'object' && 'error' in errorValue) {
                  rowData[header] = (errorValue as { error: string }).error || null;
                } else {
                  rowData[header] = null;
                }
              } else {
                rowData[header] = cell.value as ExcelCellValue;
              }
            }
          });

          const cadet = ExcelService.processRowData(rowData);
          if (cadet) {
            cadetData.push(cadet);
          }
        } catch (error) {
          console.error(`Error processing row ${rowNumber}:`, error);
        }
      });

      // console.log(`Successfully parsed ${cadetData.length} cadets from ${filePath}`);
      return cadetData;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error parsing Excel file:", errorMessage);
      throw new Error(`Failed to parse Excel file: ${errorMessage}`);
    }
  }

  /**
   * Validate Excel file structure
   */
  private static validateExcelStructure(worksheet: ExcelJS.Worksheet): void {
    const headers = worksheet.getRow(1).values as string[];
    const missingHeaders = EXCEL_HEADERS.REQUIRED.filter(header => 
      !headers.some(h => h && h.toString().includes(header))
    );

    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(", ")}`);
    }
  }

  /**
   * Process a single row of Excel data into a Cadet object
   */
  private static processRowData(rowData: ExcelRowData): Cadet | null {
    try {
      const cycleData: Record<CycleName, CycleData> = {} as Record<CycleName, CycleData>;
      
      for (const [cycleName, prefix] of Object.entries(CYCLE_PREFIXES)) {
        cycleData[cycleName as CycleName] = ExcelService.parseCycleData(rowData, prefix);
      }

      const rank = ExcelService.getNumericValue(rowData[EXCEL_HEADERS.CADET_INFO.RANK]);
      const total = ExcelService.getNumericValue(rowData[EXCEL_HEADERS.CADET_INFO.TOTAL]);
      const qualification = ExcelService.getNumericValue(rowData[EXCEL_HEADERS.CADET_INFO.QUALIFICATION]);

      const cadetBase = {
        rank,
        primaryCadet: ExcelService.getStringValue(rowData[EXCEL_HEADERS.CADET_INFO.PRIMARY_CADET]),
        agentCode: ExcelService.getStringValue(rowData[EXCEL_HEADERS.CADET_INFO.AGENT_CODE]),
        spousePartner: ExcelService.getStringValue(rowData[EXCEL_HEADERS.CADET_INFO.SPOUSE_PARTNER]),
        smd: ExcelService.getStringValue(rowData[EXCEL_HEADERS.CADET_INFO.SMD]),
        qualification,
        ...cycleData,
        total,
      };

      const isDisqualified = ExcelService.calculateDisqualificationStatus(cadetBase);
      // const isDisqualified = false; // Temporarily disable disqualification logic

      const cadet: Cadet = {
        ...cadetBase,
        isDisqualified,
        rank: isDisqualified ? 999 : rank
      };
      // // console.log(`Processed cadet: ${cadet.primaryCadet}, Rank: ${cadet.rank}, Total: ${cadet.total}, Disqualified: ${cadet.isDisqualified}`);

      return cadet;
    } catch (error) {
      // console.error("Error processing cadet row:", error);
      return null;
    }
  }

  /**
   * Parse cycle data from row data
   */
  private static parseCycleData(rowData: ExcelRowData, cyclePrefix: string): CycleData {
    const getFieldValue = (field: string): number =>
      ExcelService.getNumericValue(rowData[`${cyclePrefix} ${field}`]);

    // Only C3 uses "Bonus Income" header, all others use "Bonus Promo"
    const bonusPromoField = cyclePrefix === "C3" ? "Bonus Income" : "Bonus Promo";

    return {
      recruit: getFieldValue("Recruit"),
      sales: getFieldValue("Sales"),
      book: getFieldValue("Book"),
      bonusPromo: getFieldValue(bonusPromoField),
      bonusBBC: getFieldValue("Bonus BBC"),
      bonusEvents: getFieldValue("Bonus Events"),
      bonusBF: getFieldValue("Bonus BF"),
      bonusGX: getFieldValue("Bonus GX"),
      attend: getFieldValue("Attend"),
      penalty: getFieldValue("Penalty"),
      penaltyNotes: ExcelService.getStringValue(rowData[`${cyclePrefix} Penalty Notes`]),
      callout: getFieldValue("Callout"),
      calloutNotes: ExcelService.getStringValue(rowData[`${cyclePrefix} Callout Notes`]),
      total: getFieldValue("Total"),
    };
  }

  /**
   * Calculate if a cadet is disqualified based on business rules
   */
  static calculateDisqualificationStatus(cadet: CadetWithoutDisqualification): boolean {
    // Rule 1: Count negative attendance values across all cycles - this takes priority
    const cycleKeys: CycleName[] = ['c1', 'c2', 'c3', 'c4', 'c5'];
    const missedClasses = cycleKeys
      .map(key => cadet[key])
      .filter((cycleData): cycleData is CycleData => 
        cycleData !== undefined && typeof cycleData === 'object' && 'attend' in cycleData
      )
      .filter(cycleData => cycleData.attend < 0)
      .length;

    // If missed 2+ classes, disqualified regardless of points
    if (missedClasses >= BUSINESS_RULES.DISQUALIFICATION.MAX_MISSED_CLASSES) {
      // // console.log(`Cadet: ${cadet.primaryCadet}, Missed Classes: ${missedClasses} - DISQUALIFIED (missed classes)`);
      return true;
    }

    // Rule 2: Check if total score is <= minimum threshold
    if (cadet.total <= BUSINESS_RULES.DISQUALIFICATION.MINIMUM_TOTAL_SCORE) {
      // // console.log(`Cadet: ${cadet.primaryCadet}, Total Score: ${cadet.total} - DISQUALIFIED (low score)`);
      return true;
    }

    return false;
  }

  /**
   * Calculate if a cadet is disqualified based on business rules up to a specific class
   * This prevents retroactive disqualification from future classes affecting earlier callouts
   */
  static calculateDisqualificationStatusUpTo(cadet: CadetWithoutDisqualification, upToClass: number): boolean {
    // Define which cycles to check based on the class limit
    const cycleKeyMap: Record<number, CycleName[]> = {
      1: ['c1'],
      2: ['c1', 'c2'],
      3: ['c1', 'c2', 'c3'],
      4: ['c1', 'c2', 'c3', 'c4'],
      5: ['c1', 'c2', 'c3', 'c4', 'c5']
    };

    const cycleKeys = cycleKeyMap[upToClass] || ['c1', 'c2', 'c3', 'c4', 'c5'];

    // Rule 1: Count negative attendance values across specified cycles only
    const missedClasses = cycleKeys
      .map(key => cadet[key])
      .filter((cycleData): cycleData is CycleData => 
        cycleData !== undefined && typeof cycleData === 'object' && 'attend' in cycleData
      )
      .filter(cycleData => cycleData.attend < 0)
      .length;

    // If missed 2+ classes, disqualified regardless of points
    if (missedClasses >= BUSINESS_RULES.DISQUALIFICATION.MAX_MISSED_CLASSES) {
      return true;
    }

    // Rule 2: Calculate total score up to the specified class only
    let totalUpToClass = cadet.qualification || 0;
    
    // Add cycle totals up to the specified class
    cycleKeys.forEach(key => {
      const cycleData = cadet[key];
      if (cycleData && typeof cycleData === 'object' && 'total' in cycleData) {
        totalUpToClass += cycleData.total || 0;
      }
    });

    // Check if total score up to this class is <= minimum threshold
    if (totalUpToClass <= BUSINESS_RULES.DISQUALIFICATION.MINIMUM_TOTAL_SCORE) {
      return true;
    }

    return false;
  }

  /**
   * Validate cadet data integrity
   */
  static validateCadetData(cadets: Cadet[]): ValidationResult<Cadet> {
    const valid: Cadet[] = [];
    const invalid: Array<{ item: Cadet; reason: string }> = [];

    cadets.forEach(cadet => {
      if (!cadet.primaryCadet || cadet.primaryCadet.trim() === '') {
        invalid.push({ item: cadet, reason: 'Missing primary cadet name' });
      } else if (typeof cadet.total !== 'number' || isNaN(cadet.total)) {
        invalid.push({ item: cadet, reason: 'Invalid total score' });
      } else {
        valid.push(cadet);
      }
    });

    return { valid, invalid };
  }

  /**
   * Helper function to safely extract numeric values from Excel cells
   */
  private static getNumericValue(value: ExcelCellValue): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'boolean') return value ? 1 : 0;
    if (typeof value === 'object' && 'result' in value && typeof value.result === 'number') {
      return value.result;
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  /**
   * Helper function to safely extract string values
   */
  private static getStringValue(value: ExcelCellValue): string {
    if (value === null || value === undefined) return "";
    return String(value);
  }
}
